import { parseCreativeIdDigest } from 'hooks';
import type { CreativeEntry } from 'types/shared';
import type {
  CreateTwitterAdParams,
  TwitterAdSettingsPayload,
  TwitterCampaignWithLineItems,
  TwitterCreativePayload,
  TwitterCreativeType,
  TwitterLineItemPayload,
} from '../api/types';

export interface BuildCreateTwitterAdPayloadArgs {
  app: string;
  creativesRows: readonly CreativeEntry[];
  lineItemsData: readonly TwitterCampaignWithLineItems[];
  selectedLineItems: readonly string[];
  adSettings: Record<string, TwitterAdSettingsPayload | undefined>;
}

const toCreativeType = (value: string): TwitterCreativeType =>
  value.toLowerCase() === 'image' ? 'image' : 'video';

export const toTwitterCreativePayload = (row: CreativeEntry): TwitterCreativePayload => ({
  id: parseCreativeIdDigest(row.id) ?? row.id,
  creative_id: row.creative_id,
  creative_type: toCreativeType(row.creative_type),
  name: row.name,
});

export const toTwitterLineItemPayloads = (
  lineItemsData: readonly TwitterCampaignWithLineItems[],
  selectedLineItems: readonly string[],
): TwitterLineItemPayload[] => {
  const selected = new Set(selectedLineItems);

  return lineItemsData.flatMap((campaign) =>
    campaign.line_items
      .filter((lineItem) => selected.has(lineItem.line_item_id) && !!lineItem.flow)
      .map((lineItem) => ({
        account_id: campaign.account_id,
        campaign_id: campaign.campaign_id,
        campaign_name: campaign.campaign_name,
        line_item_id: lineItem.line_item_id,
        line_item_name: lineItem.line_item_name,
        flow: lineItem.flow!,
      })),
  );
};

export const buildCreateTwitterAdPayload = ({
  app,
  creativesRows,
  lineItemsData,
  selectedLineItems,
  adSettings,
}: BuildCreateTwitterAdPayloadArgs): CreateTwitterAdParams => {
  const line_items = toTwitterLineItemPayloads(lineItemsData, selectedLineItems);
  const flows = new Set(line_items.map((lineItem) => lineItem.flow));

  const ad_settings = Object.fromEntries(
    [...flows]
      .map((flow) => [flow, adSettings[flow]] as const)
      .filter((entry): entry is readonly [string, TwitterAdSettingsPayload] => !!entry[1])
      .map(([flow, setting]) => [
        flow,
        { text_id: String(setting.text_id), locale: setting.locale },
      ]),
  );

  return {
    app,
    creatives: creativesRows.map(toTwitterCreativePayload),
    line_items,
    ad_settings,
  };
};
